import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Users, Globe, BookOpen, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const SuperAdminDashboard: React.FC = () => {
  const [isFlushing, setIsFlushing] = useState(false);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['super-admin-stats'],
    queryFn: async () => {
      const [orgsResult, usersResult, domainsResult, resourcesResult] = await Promise.all([
        supabase.from('organizations').select('id', { count: 'exact', head: true }),
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('organization_domains').select('id', { count: 'exact', head: true }),
        supabase.from('library_items').select('id', { count: 'exact', head: true }),
      ]);

      return {
        organizations: orgsResult.count ?? 0,
        users: usersResult.count ?? 0,
        domains: domainsResult.count ?? 0,
        resources: resourcesResult.count ?? 0,
      };
    }
  });

  const { data: cacheVersion, refetch: refetchCacheVersion } = useQuery({
    queryKey: ['cache-version'],
    queryFn: async () => {
      const { data } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'CACHE_VERSION')
        .is('organization_id', null)
        .maybeSingle();
      return data?.value || '1';
    }
  });

  const handleFlushCaches = async () => {
    setIsFlushing(true);
    try {
      const currentVersion = parseInt(cacheVersion || '1', 10);
      const newVersion = (currentVersion + 1).toString();

      // Check if CACHE_VERSION exists
      const { data: existing } = await supabase
        .from('settings')
        .select('id')
        .eq('key', 'CACHE_VERSION')
        .is('organization_id', null)
        .maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('settings')
          .update({ value: newVersion, updated_at: new Date().toISOString() })
          .eq('key', 'CACHE_VERSION')
          .is('organization_id', null);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('settings')
          .insert({ key: 'CACHE_VERSION', value: newVersion, organization_id: null });

        if (error) throw error;
      }

      await refetchCacheVersion();
      toast.success(`Cache flushed! Version updated to ${newVersion}. All users will see fresh data on their next visit.`);
    } catch (err) {
      console.error('Error flushing cache:', err);
      toast.error('Failed to flush cache');
    } finally {
      setIsFlushing(false);
    }
  };

  const statCards = [
    { title: 'Organizations', value: stats?.organizations ?? 0, icon: Building2, description: 'Active tenants' },
    { title: 'Users', value: stats?.users ?? 0, icon: Users, description: 'Total registered users' },
    { title: 'Domains', value: stats?.domains ?? 0, icon: Globe, description: 'Configured domains' },
    { title: 'Resources', value: stats?.resources ?? 0, icon: BookOpen, description: 'Library items across all orgs' },
  ];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Super Admin Dashboard</h1>
        <p className="text-slate-500">Platform-wide overview and management</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.title} className="bg-white border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-800">
                {isLoading ? '...' : stat.value}
              </div>
              <p className="text-xs text-slate-500">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800">Quick Actions</CardTitle>
          <CardDescription className="text-slate-500">Common super admin tasks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Use the navigation to manage organizations, domains, users, and view cross-org analytics.
          </p>
          
          <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-700">Client Cache Management</p>
              <p className="text-xs text-slate-500">
                Current version: <span className="font-mono font-semibold">{cacheVersion || '1'}</span>
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Flush all client caches when you publish a new site version
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={isFlushing}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isFlushing ? 'animate-spin' : ''}`} />
                  Flush All Caches
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Flush All Client Caches?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will invalidate cached organization data for all users across all clients. 
                    Their next page load will fetch fresh data from the server instead of using cached data.
                    <br /><br />
                    Use this when you've published a new site version and want all users to see the updates immediately.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleFlushCaches}>
                    Flush Caches
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
